import type { StatusAbertura, StatusNoErp } from '../../api/aberturas.js';
import type { TomStatus } from '../../componentes/ChipStatus.js';
import type { OpcaoFiltro } from '../../componentes/Filtros.js';

/**
 * Como a tela lê a movimentação em duas etapas: a situação dela e onde as
 * entregas estão no caminho até o ERP.
 *
 * São duas perguntas diferentes, e a tabela mostra as duas: "a caixa já
 * chegou toda?" é a situação; "o Senior já sabe?" é o resumo no ERP. Uma
 * movimentação `Finalizada` com uma entrega `Rejeitada` terminou no chão e
 * não terminou no ERP — juntar as duas numa coluna só esconderia isso.
 */

const ROTULO_DA_SITUACAO: Readonly<Record<StatusAbertura, string>> = {
  EmAndamento: 'Em andamento',
  ParcialmenteFinalizada: 'Entregue em parte',
  Finalizada: 'Finalizada',
  EmDivergencia: 'Em divergência',
  Cancelada: 'Cancelada',
};

const TOM_DA_SITUACAO: Readonly<Record<StatusAbertura, TomStatus>> = {
  EmAndamento: 'andamento',
  ParcialmenteFinalizada: 'andamento',
  Finalizada: 'concluida',
  EmDivergencia: 'divergencia',
  Cancelada: 'desativado',
};

export function rotuloDaSituacao(status: StatusAbertura): string {
  return ROTULO_DA_SITUACAO[status];
}

export function tomDaSituacao(status: StatusAbertura): TomStatus {
  return TOM_DA_SITUACAO[status];
}

/**
 * O saldo só se cancela enquanto a movimentação recebe entrega. A em
 * divergência precisa antes da decisão sobre a entrega retida — é a mesma
 * regra do middleware (`ABERTURA_NAO_CANCELAVEL`), antecipada na tela.
 */
export function aceitaCancelamento(status: StatusAbertura): boolean {
  return status === 'EmAndamento' || status === 'ParcialmenteFinalizada';
}

export const SITUACAO_TODAS = 'todas';

const SITUACOES = Object.keys(ROTULO_DA_SITUACAO) as StatusAbertura[];

export const OPCOES_DE_SITUACAO: readonly OpcaoFiltro[] = [
  { valor: SITUACAO_TODAS, rotulo: 'Situação · todas' },
  ...SITUACOES.map((status) => ({ valor: status, rotulo: ROTULO_DA_SITUACAO[status] })),
];

/** Valor do seletor → recorte do servidor. O que não for situação não recorta. */
export function statusDoFiltro(valor: string): StatusAbertura | null {
  return (SITUACOES as readonly string[]).includes(valor) ? (valor as StatusAbertura) : null;
}

export type ParteDoResumo = {
  readonly estado: keyof StatusNoErp;
  readonly quantidade: number;
  readonly rotulo: string;
  readonly tom: TomStatus;
};

/**
 * Ordem de leitura: o que exige gente primeiro. Rejeitada no ERP e retida na
 * divergência são as que alguém precisa olhar; consolidada é o fim feliz.
 */
const ORDEM_NO_ERP: readonly {
  readonly estado: keyof StatusNoErp;
  readonly singular: string;
  readonly plural: string;
  readonly tom: TomStatus;
}[] = [
  { estado: 'Rejeitada', singular: 'rejeitada', plural: 'rejeitadas', tom: 'falha' },
  { estado: 'Retida', singular: 'retida', plural: 'retidas', tom: 'divergencia' },
  {
    estado: 'Registrada',
    singular: 'aguardando envio',
    plural: 'aguardando envio',
    tom: 'andamento',
  },
  { estado: 'Enviando', singular: 'enviando', plural: 'enviando', tom: 'andamento' },
  { estado: 'Consolidada', singular: 'consolidada', plural: 'consolidadas', tom: 'concluida' },
];

/** As entregas por estado no caminho até o ERP, só os estados que têm alguma. */
export function resumoNoErp(status: StatusNoErp): readonly ParteDoResumo[] {
  return ORDEM_NO_ERP.filter((parte) => status[parte.estado] > 0).map((parte) => {
    const quantidade = status[parte.estado];
    return {
      estado: parte.estado,
      quantidade,
      rotulo: `${String(quantidade)} ${quantidade === 1 ? parte.singular : parte.plural}`,
      tom: parte.tom,
    };
  });
}
